import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateStr: string, locale = "en-US"): string {
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(dateStr));
}

/**
 * "Sep 29" — or "Sep 29, 2027" when the date is not in the current year. For
 * tight spots (the discover sheet's fact strip) where the year of a near
 * deadline is noise that costs the cell its one line.
 */
export function formatShortDate(dateStr: string, locale = "en-US", now: Date = new Date()): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() !== now.getFullYear() && { year: "numeric" }),
  }).format(date);
}

/**
 * When a quest went up, for the feed cards (DEV-794). The first week reads as
 * a relative day ("today", "3 days ago") because freshness is what a worker
 * scanning the feed is asking; after that the date itself is the useful fact.
 *
 * Counted in CALENDAR days in the viewer's zone, not 24h blocks: a quest
 * posted at 11pm reads "yesterday" the next morning, not "today". The `kind`
 * tells the caller which sentence to wrap it in — "Posted 3 days ago" and
 * "Posted on Sep 12" are different phrases in es/pt, not one template.
 *
 * Null for an unparseable timestamp, so a card omits the line instead of
 * printing "Invalid Date".
 */
export function formatPostedDate(
  dateStr: string,
  locale = "en-US",
  now: Date = new Date(),
): { kind: "ago" | "on"; text: string } | null {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;

  const dayNumber = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000;
  // Clamped at 0: a server timestamp a few seconds ahead of the device clock
  // must not read "tomorrow".
  const days = Math.max(0, dayNumber(now) - dayNumber(date));

  if (days < 7) {
    return {
      kind: "ago",
      text: new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(-days, "day"),
    };
  }
  return { kind: "on", text: formatShortDate(dateStr, locale, now) };
}

export function formatRelativeTime(dateStr: string, locale = "en-US"): string {
  const now = Date.now();
  const diff = now - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (days > 0) return rtf.format(-days, "day");
  if (hours > 0) return rtf.format(-hours, "hour");
  if (minutes > 0) return rtf.format(-minutes, "minute");
  return rtf.format(0, "second");
}

export function generateId(): string {
  return crypto.randomUUID();
}

export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + "...";
}

export function isVolunteerQuest(budgetUSD: number): boolean {
  return Number.isFinite(budgetUSD) && budgetUSD === 0;
}

/**
 * Is this card's whole pay RANGE unpaid work?
 *
 * `isVolunteerQuest` asks it of a single figure, which is the right question
 * for one task. A quest-level discover card carries the floor and ceiling of
 * several differently priced tasks, and its floor is 0 as soon as ONE open
 * task is unpaid — `tasks.budget_usd` has no minimum, so a creator adding an
 * unpaid task to a 300 USDT quest is an ordinary action. Asking the
 * single-figure question about that floor labelled the entire quest
 * "Volunteer" and hid the 300.
 *
 * So: volunteer only when the ceiling is unpaid too. An absent ceiling means
 * "one figure" and defers entirely to the floor, which keeps every
 * single-task surface answering exactly as it did.
 */
export function isVolunteerRange(
  budgetUSD: number,
  budgetMaxUSD?: number | null,
): boolean {
  if (!isVolunteerQuest(budgetUSD)) return false;
  if (budgetMaxUSD === undefined || budgetMaxUSD === null) return true;
  return isVolunteerQuest(budgetMaxUSD);
}

export function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** Join a city and country into a display string, skipping blanks ("Asunción, Paraguay"). */
export function formatLocation(
  city: string | null | undefined,
  country: string | null | undefined,
): string {
  return [city, country].filter(Boolean).join(", ");
}
